/* FizzyIQ — Home ingredients rail (fizzy-rv-ingredients / rvi-).
   A horizontally scrollable, infinitely looping row of ingredient cards. The
   set is cloned on both sides — [cloneA][original][cloneB] — and the rail starts
   centered on the original. As the user scrolls (touch / trackpad / arrows) past
   a clone edge we reposition scrollLeft by one set-width; the content repeats
   exactly, so the jump is invisible and the rail loops forever in both directions.
   Reduced-motion: arrow paging jumps instantly (native scroll is untouched). */
(function () {
  'use strict';

  var REDUCE = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function initRail(root) {
    if (root.dataset.rviInit) return;
    root.dataset.rviInit = '1';

    var marquee = root.querySelector('.rvi-marquee');
    var set = root.querySelector('.rvi-set');
    var prev = root.querySelector('[data-rvi-prev]');
    var next = root.querySelector('[data-rvi-next]');
    if (!marquee || !set) return;

    var cards = set.children;
    if (cards.length < 2) {
      if (prev) prev.disabled = true;
      if (next) next.disabled = true;
      return;
    }

    /* Whole rail already fits — nothing to scroll, no loop. */
    if (set.scrollWidth <= marquee.clientWidth + 4) {
      if (prev) prev.disabled = true;
      if (next) next.disabled = true;
      return;
    }

    /* Clone the set before and after the original for a two-way loop. */
    var track = set.parentNode;
    var before = set.cloneNode(true);
    var after = set.cloneNode(true);
    before.classList.add('rvi-set--clone');
    after.classList.add('rvi-set--clone');
    before.setAttribute('aria-hidden', 'true');
    after.setAttribute('aria-hidden', 'true');
    track.insertBefore(before, set);
    track.appendChild(after);

    function loopWidth() { return set.offsetLeft - before.offsetLeft; }
    function cardStride() {
      if (cards.length > 1) {
        var d = cards[1].getBoundingClientRect().left - cards[0].getBoundingClientRect().left;
        if (d > 0) return d;
      }
      return cards[0].getBoundingClientRect().width + 18;
    }

    /* Reposition scrollLeft by one set-width once it drifts past a clone edge.
       The content repeats every set-width, so the shift is invisible. */
    function normalize() {
      var w = loopWidth();
      if (w <= 0) return;
      if (marquee.scrollLeft < w * 0.5) marquee.scrollLeft += w;
      else if (marquee.scrollLeft >= w * 1.5) marquee.scrollLeft -= w;
    }

    /* Start on the original (middle) set so there's a full set to each side. */
    function recenter() { marquee.scrollLeft = loopWidth(); }
    recenter();

    /* During an arrow's smooth-scroll we must NOT touch scrollLeft (that would
       cancel the animation and jump-cut at the seam) — the 3-set track has room
       to overshoot into a clone; we normalize once the move settles. */
    var arrowTimer = 0;
    var ticking = false;
    marquee.addEventListener('scroll', function () {
      if (arrowTimer || dragging || ticking) return;
      ticking = true;
      requestAnimationFrame(function () { normalize(); ticking = false; });
    }, { passive: true });

    function page(dir) {
      var amount = Math.max(cardStride(), marquee.clientWidth * 0.8) * dir;
      if (arrowTimer) clearTimeout(arrowTimer);
      arrowTimer = setTimeout(function () { arrowTimer = 0; normalize(); }, 700);
      marquee.scrollBy({ left: amount, behavior: REDUCE ? 'auto' : 'smooth' });
      if (REDUCE) { clearTimeout(arrowTimer); arrowTimer = 0; normalize(); }
    }
    if (prev) { prev.disabled = false; prev.addEventListener('click', function () { page(-1); }); }
    if (next) { next.disabled = false; next.addEventListener('click', function () { page(1); }); }

    /* Mouse drag-to-scroll (Sam 2026-08-14). Mouse-only: touch/trackpad already
       scroll the rail natively, and fighting the browser's touch physics would
       regress the 2026-06-15 ruling (vertical swipes belong to the page).
       Runs inside initRail so it can reuse normalize() — the loop-wrap must
       adjust the drag baseline by the same shift, or the card jumps mid-drag. */
    var dragging = false, dragMoved = false, dragStartX = 0, dragStartScroll = 0;
    marquee.addEventListener('pointerdown', function (e) {
      if (e.pointerType !== 'mouse' || e.button !== 0) return;
      dragging = true; dragMoved = false;
      dragStartX = e.clientX; dragStartScroll = marquee.scrollLeft;
    });
    marquee.addEventListener('pointermove', function (e) {
      if (!dragging) return;
      var dx = e.clientX - dragStartX;
      if (!dragMoved && Math.abs(dx) < 6) return; /* click tolerance */
      if (!dragMoved) {
        dragMoved = true;
        if (marquee.setPointerCapture) marquee.setPointerCapture(e.pointerId);
        marquee.classList.add('is-dragging');
      }
      e.preventDefault();
      marquee.scrollLeft = dragStartScroll - dx;
      var pos = marquee.scrollLeft;
      normalize();
      var shift = marquee.scrollLeft - pos;
      if (shift) dragStartScroll += shift;
    });
    function endDrag() {
      if (!dragging) return;
      dragging = false;
      marquee.classList.remove('is-dragging');
    }
    marquee.addEventListener('pointerup', endDrag);
    marquee.addEventListener('pointercancel', endDrag);
    /* A drag must not end by opening a card: capture-phase listener swallows the
       click that follows pointerup before initModal's bubble-phase handler. */
    marquee.addEventListener('click', function (e) {
      if (!dragMoved) return;
      dragMoved = false;
      e.preventDefault();
      e.stopPropagation();
    }, true);
    /* Firefox has no -webkit-user-drag; block native image ghost-drag here. */
    marquee.addEventListener('dragstart', function (e) { e.preventDefault(); });

    /* Recenter only when the available WIDTH changes (orientation / real resize).
       NOT on a same-width resize — mobile fires `resize` when the URL bar shows/
       hides during a page scroll, which would otherwise snap the rail back to the
       start while the user scrolls past it (Sam 2026-06-16). */
    var lastW = window.innerWidth;
    window.addEventListener('resize', function () {
      if (window.innerWidth === lastW) return;
      lastW = window.innerWidth;
      recenter();
    });
    if ('ResizeObserver' in window) {
      var lastSetW = set.offsetWidth;
      new ResizeObserver(function () {
        if (set.offsetWidth === lastSetW) return;
        lastSetW = set.offsetWidth;
        recenter();
      }).observe(set);
    }
  }

  /* Clicking a card opens the detail dialog (dose · form · source + What/Why),
     animated in. The dialog is reparented to <body> so the rail's overflow:hidden
     can't clip it. Click is delegated on the marquee so cloned cards work too. */
  function initModal(root) {
    if (root.dataset.rviModalInit) return;
    var marquee = root.querySelector('.rvi-marquee');
    var modal = root.parentNode && root.parentNode.querySelector('.rvi-modal');
    if (!marquee || !modal) return;
    root.dataset.rviModalInit = '1';

    document.body.appendChild(modal); // escape stacking / overflow clipping

    var card = modal.querySelector('.rvi-modal__card');
    var closeBtn = modal.querySelector('.rvi-modal__close');
    var f = {
      img: modal.querySelector('.rvi-modal__img'),
      media: modal.querySelector('.rvi-modal__media'),
      eyebrow: modal.querySelector('.rvi-modal__eyebrow span'),
      title: modal.querySelector('.rvi-modal__title'),
      dose: modal.querySelector('[data-f="dose"]'),
      form: modal.querySelector('[data-f="form"]'),
      source: modal.querySelector('[data-f="source"]'),
      what: modal.querySelector('[data-f="what"]'),
      why: modal.querySelector('[data-f="why"]')
    };
    var lastFocus = null;

    function text(el, sel) { var n = el.querySelector(sel); return n ? n.textContent : ''; }

    function open(srcCard) {
      lastFocus = document.activeElement;
      var img = srcCard.querySelector('.rvi-card__img');
      f.img.src = img ? img.currentSrc || img.src : '';
      f.img.alt = text(srcCard, '.rvi-card__title');
      var grad = srcCard.style.getPropertyValue('--rvi-grad') || '#2A3658';
      if (card) card.style.background = grad; // whole card takes the ingredient gradient
      f.media.style.background = 'transparent';
      f.eyebrow.textContent = text(srcCard, '.rvi-card__eyebrow');
      f.title.textContent = text(srcCard, '.rvi-card__title');
      f.dose.textContent = srcCard.dataset.dose || '';
      f.form.textContent = srcCard.dataset.form || '';
      f.source.textContent = srcCard.dataset.source || '';
      f.what.textContent = srcCard.dataset.what || '';
      f.why.textContent = srcCard.dataset.why || '';
      modal.classList.add('is-open');
      modal.setAttribute('aria-hidden', 'false');
      document.body.classList.add('rvi-modal-lock');
      if (card) card.scrollTop = 0;
      if (closeBtn && closeBtn.focus) closeBtn.focus({ preventScroll: true });
    }
    function close() {
      modal.classList.remove('is-open');
      modal.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('rvi-modal-lock');
      if (lastFocus && lastFocus.focus) lastFocus.focus({ preventScroll: true });
    }

    marquee.addEventListener('click', function (e) {
      var c = e.target.closest('.rvi-card');
      if (c) open(c);
    });
    marquee.addEventListener('keydown', function (e) {
      if (e.key !== 'Enter' && e.key !== ' ' && e.key !== 'Spacebar') return;
      var c = e.target.closest('.rvi-card');
      if (c) { e.preventDefault(); open(c); }
    });
    modal.addEventListener('click', function (e) {
      // close on the X / backdrop, or any click outside the detail card
      if (e.target.closest('[data-rvi-close]') || !e.target.closest('.rvi-modal__card')) close();
    });
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && modal.classList.contains('is-open')) close();
    });
  }

  function initSection(root) {
    initRail(root);
    initModal(root);
  }

  function initAll() {
    var sections = document.querySelectorAll('.rvi');
    for (var i = 0; i < sections.length; i++) initSection(sections[i]);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', function (event) {
    var root = event.target && event.target.querySelector('.rvi');
    if (root) initSection(root);
  });
})();
